import { has } from '@/commonFunctions/utils'
import { ArrowLeft, KeyboardArrow, More, UserIconFilled } from '@/components/icons'
import { MatchByPlayType } from '@/components/match/MatchByPlayType'
import { ConfirmModal } from '@/components/modal'
import { CLUB_MODIFY, isDisableMatchEdit, isExternallyManagedMatch } from '@/constants/Common'
import { ROUTE_CLUBS, ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { ScoreTeam } from '@/pages/event/components/ScoreTeam'
import { userSelector } from '@/pages/user/selectors'
import { useDeleteClubMatch, useGetPlayerProfile } from '@/services'
import { Match, PayloadDeleteClubMatch, Player, Team } from '@/types'
import { Menu, Transition } from '@headlessui/react'
import moment from 'moment'
import { Fragment, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { clubSelector } from '../selectors'

type Props = {
  match: Match
  callbackFetch?: () => void
}

export const ClubMatchItem = (props: Props) => {
  return (
    <MatchByPlayType
      matchPlayType={props.match.matchPlayType}
      matchDate={props.match.eventDate}
      adjustmentData={{ eventName: props.match.eventName, match: props.match }}
      regular={() => <ClubMatchItemRegular {...props} />}
    />
  )
}

const ClubMatchItemRegular = ({ match, callbackFetch }: Props) => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const params = useParams()

  const { permissions } = useAppSelector(clubSelector)
  const { user } = useAppSelector(userSelector)

  const { clubId } = params

  const { mutate: deleteClubMatch } = useDeleteClubMatch()

  const [deleteMatchDialog, setDeleteMatchDialog] = useState(false)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const [team1, team2] = [...match.teams].sort((team1, team2) => team1.serial - team2.serial)

  const externallyManaged = isExternallyManagedMatch(match.matchSource)
  const externalTooltipId = `external-match-tooltip-${match?.matchId}`

  const { data: t1Player1 } = useGetPlayerProfile(team1.player1.id.toString(), isExpanded)
  const { data: t2Player1 } = useGetPlayerProfile(team2.player1.id.toString(), isExpanded)
  const { data: t1Player2 } = useGetPlayerProfile(team1.player2?.id.toString(), isExpanded && Boolean(team1.player2))
  const { data: t2Player2 } = useGetPlayerProfile(team2.player2?.id.toString(), isExpanded && Boolean(team2.player2))

  const toggleDeleteMatchDialog = () => setDeleteMatchDialog(!deleteMatchDialog)

  const onNavigateProfile = (playerId: number) => navigate(`${ROUTE_PLAYER_PROFILE}/${playerId}`)

  const navigateEditMatch = () => navigate(`${ROUTE_CLUBS}/${clubId}/match/edit/${match?.matchId}`)

  const handleDeleteClubMatch = () => {
    const payload: PayloadDeleteClubMatch = {
      clubId: Number(clubId),
      matchId: match?.id,
      matchIdType: 'OBFUSCATED_MATCH_ID',
      reason: 'Match Deleted by',
      requestedByDuprId: user?.referralCode as string,
    }
    deleteClubMatch(payload, {
      onSuccess: () => {
        toggleDeleteMatchDialog()
        if (callbackFetch) callbackFetch()
      },
    })
  }

  const copyIdToClipboard = () => {
    navigator.clipboard.writeText(match?.displayIdentity)
    toast.success('Match ID Copied To Clipboard')
  }

  const renderTeam = (
    team: Team,
    teamNumber: 1 | 2,
    opponentTeam: Team,
    player1: Player | undefined,
    player2: Player | undefined
  ) => {
    return (
      <div className={`flex ${teamNumber === 1 ? 'flex-col' : 'flex-col-reverse'} w-full gap-y-2`}>
        <div className={`flex flex-col sm:flex-row w-full gap-y-2 sm:gap-y-0 sm:gap-x-2`}>
          <div className="flex flex-row w-full gap-x-3 items-center border-1 border-navy-900 border-[1px] p-2 rounded-xl border-opacity-5">
            {team.player1.imageUrl ? (
              <img src={team.player1.imageUrl} className={'h-10 w-10 rounded-full'} alt="avatar" />
            ) : (
              <UserIconFilled className="h-10 w-10 min-w-[40px]" />
            )}
            <div className="flex flex-col gap-y-1">
              <p className={`text-sm text-navy-900 font-semibold`}>{team.player1.fullName}</p>
              {player1 && (
                <p className="text-xs text-navy-900 text-opacity-50">
                  {player1.age}
                  {player1.gender ? ` • ${player1.gender[0]}` : ''}
                  {player1.shortAddress ? ` • ${player1.shortAddress}` : ''}
                </p>
              )}
            </div>
          </div>

          {team.player2 && player2 && (
            <div className="flex flex-row w-full gap-x-3 items-center border-1 border-navy-900 border-[1px] p-2 rounded-xl border-opacity-5">
              {team.player2.imageUrl ? (
                <img src={team.player2.imageUrl} className={'h-10 w-10 rounded-full'} alt="avatar" />
              ) : (
                <UserIconFilled className="h-10 w-10 min-w-[40px]" />
              )}
              <div className="flex flex-col gap-y-1">
                <p className={`text-sm text-navy-900 font-semibold`}>{team.player2.fullName}</p>
                {player2 && (
                  <p className="text-xs text-navy-900 text-opacity-50">
                    {player2.age}
                    {player2.gender ? ` • ${player2.gender[0]}` : ''}
                    {player2.shortAddress ? ` • ${player2.shortAddress}` : ''}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-row items-center justify-center">
          <div className="w-9 h-7 flex items-center justify-center">
            <p
              className={`font-medium text-xl text-navy-900 ${
                team.game1 > opponentTeam.game1 ? '' : 'text-opacity-50'
              }`}
            >
              {team.game1}
            </p>
          </div>
          {team.game2 && team.game2 !== -1 && (
            <div className="w-9 h-7 flex items-center justify-center">
              <p
                className={`font-medium text-xl text-navy-900 ${
                  team.game2 > opponentTeam.game2 ? '' : 'text-opacity-50'
                }`}
              >
                {team.game2}
              </p>
            </div>
          )}
          {team.game3 && team.game3 !== -1 && (
            <div className="w-9 h-7 flex items-center justify-center">
              <p
                className={`font-medium text-xl text-navy-900 ${
                  team.game3 > opponentTeam.game3 ? '' : 'text-opacity-50'
                }`}
              >
                {team.game3}
              </p>
            </div>
          )}
          {team.game4 && team.game4 !== -1 && (
            <div className="w-9 h-7 flex items-center justify-center">
              <p
                className={`font-medium text-xl text-navy-900 ${
                  team.game4 > opponentTeam.game4 ? '' : 'text-opacity-50'
                }`}
              >
                {team.game4}
              </p>
            </div>
          )}
          {team.game5 && team.game5 !== -1 && (
            <div className="w-9 h-7 flex items-center justify-center">
              <p
                className={`font-medium text-xl text-navy-900 ${
                  team.game5 > opponentTeam.game5 ? '' : 'text-opacity-50'
                }`}
              >
                {team.game5}
              </p>
            </div>
          )}
          <div className="w-9 h-7 flex items-center justify-center">
            <ArrowLeft className={`md:w-[6px] md:h-3 ${team.winner ? '' : 'opacity-0'}`} />
          </div>
        </div>
      </div>
    )
  }

  const renderExpandedContent = () => {
    return (
      <div className="flex flex-col mt-3 gap-x-4">
        <div className="flex flex-col gap-y-2">
          <span className="text-sm font-semibold text-navy-900">{match.location}</span>
          <span className="text-xs font-medium text-navy-900 text-opacity-50 truncate">{match.eventName}</span>
        </div>

        <div className="flex flex-col mt-3 w-full">
          {renderTeam(team1, 1, team2, t1Player1, t1Player2)}
          {renderTeam(team2, 2, team1, t2Player1, t2Player2)}
        </div>
      </div>
    )
  }

  const renderContent = () => {
    return (
      <div className="mt-3">
        <div className="flex">
          <div className="flex flex-1 flex-col">
            <div className="flex items-center py-1 cursor-pointer" onClick={() => onNavigateProfile(team1.player1.id)}>
              {team1?.player1?.imageUrl ? (
                <img src={team1?.player1?.imageUrl} className="md:w-5 md:h-5 w-4 h-4 rounded-full" alt="" />
              ) : (
                <UserIconFilled className="md:w-5 md:h-5 w-4 h-4 text-primary-500" />
              )}
              <span className={`ml-2 text-xs md:text-[13px] md:leading-5 text-navy-900 font-medium`}>
                {team1?.player1?.fullName}
              </span>
            </div>
            {team1?.player2 && (
              <div
                className="flex items-center py-1 cursor-pointer"
                onClick={() => onNavigateProfile(team1.player2.id)}
              >
                {team1?.player2?.imageUrl ? (
                  <img src={team1?.player2?.imageUrl} className="md:w-5 md:h-5 w-4 h-4 rounded-full" alt="" />
                ) : (
                  <UserIconFilled className="md:w-5 md:h-5 w-4 h-4 text-primary-500" />
                )}
                <span className={`ml-2 text-xs md:text-[13px] md:leading-5 text-navy-900 font-medium`}>
                  {team1?.player2?.fullName}
                </span>
              </div>
            )}
          </div>

          <ScoreTeam team={team1} opponentTeam={team2} />
        </div>

        <div className="h-[0.5px] bg-black bg-opacity-10 md:my-2 my-1" />

        <div className="flex">
          <div className="flex flex-1 flex-col">
            <div className="flex items-center py-1 cursor-pointer" onClick={() => onNavigateProfile(team2.player1.id)}>
              {team2?.player1?.imageUrl ? (
                <img src={team2?.player1?.imageUrl} className="md:w-5 md:h-5 w-4 h-4 rounded-full" alt="" />
              ) : (
                <UserIconFilled className="md:w-5 md:h-5 w-4 h-4 text-primary-500" />
              )}
              <span className={`ml-2 text-xs md:text-[13px] md:leading-5 text-navy-900 font-medium`}>
                {team2?.player1?.fullName}
              </span>
            </div>
            {team2?.player2 && (
              <div
                className="flex items-center py-1 cursor-pointer"
                onClick={() => onNavigateProfile(team2.player2.id)}
              >
                {team2?.player2?.imageUrl ? (
                  <img src={team2?.player2?.imageUrl} className="md:w-5 md:h-5 w-4 h-4 rounded-full" alt="" />
                ) : (
                  <UserIconFilled className="md:w-5 md:h-5 w-4 h-4 text-primary-500" />
                )}
                <span className={`ml-2 text-xs md:text-[13px] md:leading-5 text-navy-900 font-medium`}>
                  {team2?.player2?.fullName}
                </span>
              </div>
            )}
          </div>

          <ScoreTeam team={team2} opponentTeam={team1} />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="bg-white rounded-2xl p-4 md:py-4 md:px-6">
        <div className="flex justify-between items-center">
          <span className="text-xs font-semibold text-navy-900">{moment(match?.eventDate).format('MMMM D, YYYY')}</span>
          <div className="flex gap-x-2 items-center">
            {has(permissions, CLUB_MODIFY) && !isDisableMatchEdit && (
              <Menu as="div" className="relative inline-block text-left w-6 h-6">
                <Menu.Button>
                  <More className="w-6 h-6" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute z-50 top-0 right-0 mt-2 w-36 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="flex flex-col gap-y-1 px-1 py-1 ">
                      <Menu.Item disabled={externallyManaged}>
                        {({ active }) => (
                          <button
                            className={`font-medium w-full text-left rounded px-3 py-2 text-sm ${
                              externallyManaged
                                ? 'text-navy-900 text-opacity-40 cursor-not-allowed'
                                : `text-navy-900 ${active ? 'bg-slate-300' : ''}`
                            } `}
                            onClick={externallyManaged ? undefined : navigateEditMatch}
                            data-tooltip-id={externallyManaged ? externalTooltipId : undefined}
                            data-tooltip-content={externallyManaged ? t('common.externalMatchManaged') : undefined}
                          >
                            {t('common.editMatch')}
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item disabled={externallyManaged}>
                        {({ active }) => (
                          <button
                            className={`font-medium w-full text-left rounded px-3 py-2 text-sm ${
                              externallyManaged
                                ? 'text-navy-900 text-opacity-40 cursor-not-allowed'
                                : `text-navy-900 ${active ? 'bg-slate-300' : ''}`
                            } `}
                            onClick={externallyManaged ? undefined : toggleDeleteMatchDialog}
                            data-tooltip-id={externallyManaged ? externalTooltipId : undefined}
                            data-tooltip-content={externallyManaged ? t('common.externalMatchManaged') : undefined}
                          >
                            {t('common.deleteMatch')}
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
                {externallyManaged && <Tooltip id={externalTooltipId} place="left" className="max-w-[220px] z-50" />}
              </Menu>
            )}
          </div>
        </div>

        {isExpanded ? renderExpandedContent() : renderContent()}

        <div className="flex flex-row justify-between mt-3 items-center">
          {match?.displayIdentity && (
            <button onClick={copyIdToClipboard} className="text-xs font-semibold text-navy-900 text-opacity-50">
              {t('common.matchId')} {match?.displayIdentity}
            </button>
          )}

          <KeyboardArrow
            className={`duration-200 opacity-50 cursor-pointer ${isExpanded ? 'rotate-180' : ''}`}
            onClick={() => setIsExpanded((prev) => !prev)}
          />
        </div>
      </div>
      <ConfirmModal
        open={deleteMatchDialog}
        onClose={toggleDeleteMatchDialog}
        title={t('common.deleteMatch')}
        desc={t('common.deleteMatchDesc')}
        cancelTitle={t('common.cancel')}
        submitTitle={t('common.submit')}
        onSubmit={handleDeleteClubMatch}
      />
    </>
  )
}
